/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AiAgentsPage.tsx
 🗓️ Created: 2026-01-17
 🧩 Description:
    AI Agents management page.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Edit, Trash2, History, Power, PowerOff } from 'lucide-react';

import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { useDataGrid, type DataGridFetchResult } from '../../hooks/useDataGrid';
import { ModulePageContainer } from '../../components/layouts';
import { getAiAgents, updateAiAgent, deleteAiAgent, type AiAgent } from './api/aiAgentsService';

export default function AiAgentsPage() {
    const { t, refreshKey, i18n } = useI18n();
    const navigate = useNavigate();
    const { showSuccess, showError } = useNotifications();

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [agentToDelete, setAgentToDelete] = useState<AiAgent | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const translateRef = useRef(t);
    const showSuccessRef = useRef(showSuccess);
    const showErrorRef = useRef(showError);

    useEffect(() => {
        translateRef.current = t;
    }, [t, refreshKey, i18n.language]);

    useEffect(() => {
        showSuccessRef.current = showSuccess;
        showErrorRef.current = showError;
    }, [showSuccess, showError]);

    const fetchAgents = useCallback(async (params: {
        page: number;
        limit: number;
        sortBy: string;
        sortOrder: 'asc' | 'desc';
        search?: string;
    }): Promise<DataGridFetchResult> => {
        try {
            const response = await getAiAgents({ ...params, agentType: 'voice' });
            return {
                data: response.data || [],
                total: response.meta?.total || 0
            };
        } catch (error) {
            console.error('Failed to fetch AI agents:', error);
            return { data: [], total: 0 };
        }
    }, []);

    const availableColumns = [
        { key: 'extension', label: t('aiAgents.form.extension') || 'Extension', visible: true, sortable: true },
        { key: 'agentName', label: t('aiAgents.columns.name') || 'Name', visible: true, sortable: true },
        { key: 'description', label: t('aiAgents.columns.description') || 'Description', visible: true, sortable: false },
        { key: 'voice', label: t('aiAgents.columns.voice') || 'Voice', visible: true, sortable: true },
        { key: 'conversationMode', label: t('aiAgents.columns.mode') || 'Mode', visible: true, sortable: true },
        { key: 'bargeInEnabled', label: t('aiAgents.columns.bargeIn') || 'Barge-In', visible: true, sortable: false },
        { key: 'enabled', label: t('aiAgents.columns.enabled') || 'Enabled', visible: true, sortable: true },
        { key: 'actions', label: t('common.actions') || 'Actions', visible: true, sortable: false, align: 'center' as const },
    ];

    const {
        data,
        loading,
        columns,
        pagination,
        searchQuery,
        sortBy,
        sortOrder,
        handleSearch,
        handleSort,
        handlePageChange,
        handleItemsPerPageChange,
        handleColumnToggle,
        refresh
    } = useDataGrid({
        fetchData: fetchAgents,
        initialColumns: availableColumns,
        initialSortBy: 'agent_name',
        initialSortOrder: 'asc',
        initialLimit: 10,
        moduleName: 'aiAgents'
    });

    const handleCreate = useCallback(() => {
        navigate('/admin/ai-integration/agents/new');
    }, [navigate]);

    const handleEdit = useCallback((agent: AiAgent) => {
        navigate(`/admin/ai-integration/agents/${agent.uuid ?? agent.id}`);
    }, [navigate]);

    const handleViewCalls = useCallback((agent: AiAgent) => {
        navigate(`/admin/ai-integration/agents/${agent.uuid ?? agent.id}/calls`);
    }, [navigate]);

    const handleDelete = useCallback((agent: AiAgent) => {
        setAgentToDelete(agent);
        setShowDeleteModal(true);
    }, []);

    const handleToggleEnabled = useCallback(async (agent: AiAgent) => {
        try {
            await updateAiAgent(agent.id, { enabled: !agent.enabled });
            showSuccessRef.current?.(
                agent.enabled
                    ? (t('aiAgents.messages.disabled') || 'Agent disabled')
                    : (t('aiAgents.messages.enabled') || 'Agent enabled')
            );
            refresh();
        } catch (error) {
            showErrorRef.current?.(t('aiAgents.messages.updateError') || 'Failed to update agent');
        }
    }, [t, refresh]);

    const confirmDelete = useCallback(async () => {
        if (!agentToDelete) return;

        try {
            setIsDeleting(true);
            await deleteAiAgent(agentToDelete.id);
            setShowDeleteModal(false);
            setAgentToDelete(null);
            showSuccessRef.current?.(t('aiAgents.messages.deleteSuccess') || 'Agent deleted');
            refresh();
        } catch (error) {
            showErrorRef.current?.(error?.message || t('aiAgents.messages.deleteError') || 'Failed to delete');
        } finally {
            setIsDeleting(false);
        }
    }, [agentToDelete, refresh, t]);

    const getColumnRenderer = (key: string) => {
        switch (key) {
            case 'bargeInEnabled':
            case 'enabled':
                return (value: boolean) => (
                    <span className={`px-2 py-1 rounded text-xs ${value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {value ? (t('common.yes') || 'Yes') : (t('common.no') || 'No')}
                    </span>
                );
            case 'voice':
                return (value: string) => (
                    <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800 capitalize">
                        {value}
                    </span>
                );
            case 'conversationMode':
                return (value: string) => (
                    <span className={`px-2 py-1 rounded text-xs ${value === 'fast' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                        {value === 'fast' ? (t('aiAgents.mode.fast') || 'Fast') : (t('aiAgents.mode.natural') || 'Natural')}
                    </span>
                );
            default:
                return undefined;
        }
    };

    const renderActions = useCallback((agent: AiAgent) => (
        <div className="flex items-center gap-2">
            <SimpleTooltip content={t('common.edit') || 'Edit'} position="top">
                <button
                    onClick={() => handleEdit(agent)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </button>
            </SimpleTooltip>

            <SimpleTooltip content={t('aiAgents.actions.viewCalls') || 'View Calls'} position="top">
                <button
                    onClick={() => handleViewCalls(agent)}
                    className="p-2 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <History className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </button>
            </SimpleTooltip>

            <SimpleTooltip content={agent.enabled ? (t('common.disable') || 'Disable') : (t('common.enable') || 'Enable')} position="top">
                <button
                    onClick={() => handleToggleEnabled(agent)}
                    className={`p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60 ${agent.enabled
                        ? 'hover:bg-green-100 dark:hover:bg-green-900/30'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                >
                    {agent.enabled ? (
                        <Power className="h-4 w-4 text-green-600 dark:text-green-400" />
                    ) : (
                        <PowerOff className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    )}
                </button>
            </SimpleTooltip>

            <SimpleTooltip content={t('common.delete') || 'Delete'} position="top">
                <button
                    onClick={() => handleDelete(agent)}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                </button>
            </SimpleTooltip>
        </div>
    ), [handleEdit, handleViewCalls, handleToggleEnabled, handleDelete, t]);

    return (
        <ModulePageContainer
            currentView="list"
            listView={
                <DataGridSimple
                    data={data}
                    columns={columns.map(column => ({
                        ...column,
                        render: getColumnRenderer(column.key) || column.render
                    }))}
                    onColumnToggle={handleColumnToggle}
                    loading={loading}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    searchQuery={searchQuery}
                    onSearch={handleSearch}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    onAdd={handleCreate}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    title={t('aiAgents.title') || 'AI Agents'}
                    titleIcon={<Bot className="h-5 w-5 text-primary" />}
                    addButtonText={t('aiAgents.actions.add') || 'Add'}
                    searchPlaceholder={t('aiAgents.searchPlaceholder') || 'Search agents...'}
                    emptyMessage={t('common.noItemsFound') || 'No items found'}
                    renderActions={renderActions}
                    translations={{
                        items: t('common.items') || 'items',
                        columns: t('common.columns') || 'Columns',
                        noItemsFound: t('common.noItemsFound') || 'No items found',
                        showing: t('common.showing') || 'Showing',
                        to: t('common.to') || 'to',
                        of: t('common.of') || 'of',
                        results: t('common.results') || 'results',
                        previous: t('common.previous') || 'Previous',
                        next: t('common.next') || 'Next',
                        page: t('common.page') || 'Page',
                        deleteItem: t('common.deleteItem') || 'Delete Item',
                        areYouSureDeleteItem: t('common.areYouSureDeleteItem') || 'Are you sure?',
                        delete: t('common.delete') || 'Delete',
                        cancel: t('common.cancel') || 'Cancel',
                        allSelected: t('common.allSelected') || 'All selected',
                        selected: t('common.selected') || 'selected',
                        searchOptions: t('common.searchOptions') || 'Search options',
                        noOptionsFound: t('common.noOptionsFound') || 'No options',
                        selectAll: t('common.selectAll') || 'Select all',
                        clearAll: t('common.clearAll') || 'Clear all',
                    }}
                />
            }
            formView={null}
        >
            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title={t('aiAgents.actions.delete') || 'Delete Agent'}
                message={
                    agentToDelete
                        ? (t('aiAgents.messages.deleteConfirm', { name: agentToDelete.agentName }) || `Delete ${agentToDelete.agentName}?`)
                        : ''
                }
                confirmText={t('common.delete') || 'Delete'}
                cancelText={t('common.cancel') || 'Cancel'}
                isLoading={isDeleting}
                type="danger"
            />
        </ModulePageContainer>
    );
}
